import { useState } from 'react';
import { useCities } from '@/hooks/use-cities';
import { useCommunities } from '@/hooks/use-communities';
import { translateInitiative } from '@/lib/api/utility';
import { useToast } from '@/components/ui/toast';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type {
  CreateInitiativeInput,
  InitiativeScope,
  InitiativeTemplate,
} from '@/types/initiative';

interface WizardStepBasicInfoProps {
  locale: Locale;
  initialData?: Partial<CreateInitiativeInput>;
  /**
   * The selected template — used to seed default descriptions per type.
   * Backend used to overwrite walk/community-shops descriptions; descriptions
   * are now editable at create time and default to the template's description.
   */
  template?: InitiativeTemplate | null;
  onValidSubmit: (data: CreateInitiativeInput) => void;
  onBack: () => void;
}

interface FormErrors {
  title_ar?: string;
  title_en?: string;
  description_ar?: string;
  description_en?: string;
  scope?: string;
  city_id?: string;
  community_id?: string;
}

const DEFAULT_DESCRIPTIONS: Record<string, { en: string; ar: string }> = {
  walk_initiative: {
    en: "Encourage your community to walk together — track distance, reward steps, and celebrate active participants.",
    ar: "شجِّع مجتمعك على المشي معاً — تتبّع المسافة، كافئ الخطوات، واحتفِ بالمشاركين النشطين.",
  },
  juthoor_initiative: {
    en: "Plant trees, grow Juthoor — sponsor planters in protected lands and watch your impact take root.",
    ar: "ازرع الأشجار، أنبِت جذور — اكفل غرّاسين في أراضٍ محميّة وشاهد أثرك يتجذّر.",
  },
};

function pickDefaultDescription(template?: InitiativeTemplate | null) {
  if (!template) return { en: '', ar: '' };
  const fromMap = DEFAULT_DESCRIPTIONS[template.slug];
  if (fromMap) return fromMap;
  return {
    en: template.description_en || '',
    ar: template.description_ar || '',
  };
}

export function WizardStepBasicInfo({
  locale,
  initialData,
  template,
  onValidSubmit,
  onBack,
}: WizardStepBasicInfoProps) {
  const defaults = pickDefaultDescription(template);
  const [titleAr, setTitleAr] = useState(initialData?.title_ar || '');
  const [titleEn, setTitleEn] = useState(initialData?.title_en || '');
  const [descriptionAr, setDescriptionAr] = useState(
    initialData?.description_ar || defaults.ar,
  );
  const [descriptionEn, setDescriptionEn] = useState(
    initialData?.description_en || defaults.en,
  );
  const [scope, setScope] = useState<InitiativeScope>(initialData?.scope || 'cooperative');
  const [cityId, setCityId] = useState(initialData?.city_id || '');
  const [communityIds, setCommunityIds] = useState<string[]>(initialData?.community_ids || []);
  const [errors, setErrors] = useState<FormErrors>({});

  const { cities } = useCities();
  const { communities } = useCommunities(cityId || undefined);
  const { toast } = useToast();
  const [translating, setTranslating] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!titleAr.trim() || titleAr.trim().length < 3) {
      newErrors.title_ar = t(locale, 'requiredField');
    }
    if (!titleEn.trim() || titleEn.trim().length < 3) {
      newErrors.title_en = t(locale, 'requiredField');
    }
    if (!cityId) {
      newErrors.city_id = t(locale, 'requiredField');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAutoTranslate = async () => {
    if (!titleEn.trim()) return;
    setTranslating(true);
    try {
      const res = await translateInitiative({ text: titleEn, target_language: 'ar' });
      if (res.success && res.data?.translations) {
        setTitleAr(res.data.translations);
      } else {
        toast(locale === 'ar' ? 'فشل في الترجمة' : 'Translation failed', 'error');
      }
    } catch {
      toast(locale === 'ar' ? 'فشل في الترجمة' : 'Translation failed', 'error');
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onValidSubmit({
      template_id: initialData?.template_id || '',
      title_ar: titleAr.trim(),
      title_en: titleEn.trim(),
      description_ar: descriptionAr.trim(),
      description_en: descriptionEn.trim(),
      scope,
      city_id: cityId,
      community_ids: communityIds,
    });
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
      hasError
        ? 'border-red-400 focus:ring-red-400/50 focus:border-red-400'
        : 'border-gray-200 dark:border-gray-700 focus:ring-primary/50 focus:border-primary'
    } rounded-xl focus:ring-2 outline-none transition-all text-sm`;

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  const getLocalizedCityName = (city: { name_ar: string; name_en: string }) =>
    locale === 'ar' ? city.name_ar : city.name_en;

  const getLocalizedCommunityName = (c: { name_ar: string; name_en: string }) =>
    locale === 'ar' ? c.name_ar : c.name_en;

  return (
    <div className="space-y-6">
      {/* Title fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="title-ar" className={labelClass.replace(' mb-1', '')}>
              {t(locale, 'initiativeTitle')} <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating || !titleEn.trim()}
              className="text-xs text-primary hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              <span className="material-icons-outlined text-sm">translate</span>
              {translating ? (locale === 'ar' ? 'جارٍ الترجمة...' : 'Translating...') : t(locale, 'autoTranslate')}
            </button>
          </div>
          <input
            id="title-ar"
            type="text"
            dir="rtl"
            value={titleAr}
            onChange={(e) => setTitleAr(e.target.value)}
            placeholder={t(locale, 'initiativeTitle')}
            className={inputClass(!!errors.title_ar)}
            aria-describedby={errors.title_ar ? 'title-ar-error' : undefined}
          />
          {errors.title_ar && (
            <p id="title-ar-error" className="mt-1 text-xs text-red-500">{errors.title_ar}</p>
          )}
        </div>
        <div>
          <label htmlFor="title-en" className={labelClass}>
            {t(locale, 'initiativeTitleEn')} <span className="text-red-500">*</span>
          </label>
          <input
            id="title-en"
            type="text"
            dir="ltr"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            placeholder="Enter title in English"
            className={inputClass(!!errors.title_en)}
            aria-describedby={errors.title_en ? 'title-en-error' : undefined}
          />
          {errors.title_en && (
            <p id="title-en-error" className="mt-1 text-xs text-red-500">{errors.title_en}</p>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="description-ar" className={labelClass}>
            {locale === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
          </label>
          <textarea
            id="description-ar"
            dir="rtl"
            rows={4}
            value={descriptionAr}
            onChange={(e) => setDescriptionAr(e.target.value)}
            placeholder={defaults.ar}
            className={`${inputClass(false)} resize-y min-h-[110px]`}
          />
        </div>
        <div>
          <label htmlFor="description-en" className={labelClass}>
            {locale === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
          </label>
          <textarea
            id="description-en"
            dir="ltr"
            rows={4}
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
            placeholder={defaults.en}
            className={`${inputClass(false)} resize-y min-h-[110px]`}
          />
        </div>
      </div>

      {/* Scope */}
      <div>
        <label htmlFor="scope" className={labelClass}>
          {t(locale, 'initiativeScope')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            id="scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as InitiativeScope)}
            className={`${inputClass()} appearance-none bg-none pe-10`}
          >
            <option value="cooperative">{t(locale, 'scopeCooperative')}</option>
            <option value="individual">{t(locale, 'scopeIndividual')}</option>
            <option value="institutional">{t(locale, 'scopeInstitutional')}</option>
          </select>
          <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400">
            expand_more
          </span>
        </div>
      </div>

      {/* Location */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-4 text-start">
          {t(locale, 'locationScope')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="city" className={`text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1`}>
              {t(locale, 'cityField')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="city"
                value={cityId}
                onChange={(e) => {
                  setCityId(e.target.value);
                  setCommunityIds([]);
                }}
                className={`${inputClass(!!errors.city_id)} appearance-none bg-none pe-10`}
                aria-describedby={errors.city_id ? 'city-error' : undefined}
              >
                <option value="">{t(locale, 'chooseOption')}</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {getLocalizedCityName(city)}
                  </option>
                ))}
              </select>
              <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400">expand_more</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4 pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          className="px-12 py-3 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark shadow-lg shadow-primary/30 transition-all"
        >
          {t(locale, 'next')}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="px-10 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
        >
          {t(locale, 'previous')}
        </button>
      </div>
    </div>
  );
}
